/**
 * Texas Made Shade — Series → Product Map
 *
 * Single source of truth for "what products live under each badge."
 * Used by /series, the header Series mega panel, and the home / city badge
 * grids so a customer who sees a badge immediately understands the market it
 * serves and the products it covers.
 */

export type SeriesProduct = { label: string; to: string };

export type SeriesGroupKey = "residential" | "commercial" | "ag-rec" | "specialty";

export interface SeriesProductEntry {
  slug: string;
  /** Badge landing page */
  path: string;
  /** Short badge name, e.g. "Alamo" */
  name: string;
  /** Full trademarked series name */
  fullName: string;
  /** Area of business shown under the badge, e.g. "Commercial" */
  market: string;
  /** Who it serves — one short line */
  audience: string;
  /** Products / services under this badge, each linking to its page */
  products: SeriesProduct[];
  group: SeriesGroupKey;
}

export const SERIES_GROUPS: { key: SeriesGroupKey; title: string; blurb: string }[] = [
  {
    key: "residential",
    title: "Residential & Outdoor Living",
    blurb: "Homes, patios, backyards and premium outdoor living spaces.",
  },
  {
    key: "commercial",
    title: "Commercial, Hospitality & Institutional",
    blurb: "Restaurants, retail, hotels and resorts, schools, churches and government.",
  },
  {
    key: "ag-rec",
    title: "Agriculture, Ranch & Recreation",
    blurb: "Ranch and farm covers, trailers, hunting, fishing and boating.",
  },
  {
    key: "specialty",
    title: "Specialty, Service & Automation",
    blurb: "Recover and repair, welding and fabrication, smart lighting, automation and memberships.",
  },
];

export const SERIES_PRODUCTS: SeriesProductEntry[] = [
  {
    slug: "alamo-series",
    path: "/alamo-series",
    name: "Alamo",
    fullName: "Alamo Series™",
    market: "Residential Awnings & Shade",
    audience: "Homeowners across San Antonio, Austin, New Braunfels and the Hill Country.",
    group: "residential",
    products: [
      { label: "Custom Awnings", to: "/custom-awnings" },
      { label: "Residential Awnings", to: "/residential-awnings" },
      { label: "Fabric Awnings", to: "/fabric-awnings" },
      { label: "Metal Awnings", to: "/metal-awnings" },
      { label: "Patio Covers", to: "/pergola-covers" },
      { label: "Pergola Covers", to: "/pergola-covers" },
      { label: "Gazebo Covers & Privacy Panels", to: "/gazebo-privacy-panels" },
      { label: "Sail Shades", to: "/sail-shades" },
      { label: "Outdoor Curtains & Drop Curtains", to: "/curtains" },
      { label: "Pool & Spa Shade", to: "/pool-spa" },
      { label: "Outdoor Heating & Cooling", to: "/outdoor-hvac" },
    ],
  },
  {
    slug: "hill-country-series",
    path: "/hill-country-series",
    name: "Hill Country",
    fullName: "Hill Country Series™",
    market: "Outdoor Living & Premium Shade",
    audience: "Homeowners building premium, motorized outdoor living spaces.",
    group: "residential",
    products: [
      { label: "Motorized Louvered Pergolas", to: "/louvered-pergolas" },
      { label: "Solair® Retractable Awnings", to: "/retractable-awnings" },
      { label: "Motorized Patio Screens", to: "/exterior-vertical-shades" },
      { label: "Exterior Vertical Shades / Drop Screens", to: "/exterior-vertical-shades" },
      { label: "Patio Shades & Curtains", to: "/curtains" },
      { label: "Sail Shades", to: "/sail-shades" },
      { label: "Outdoor Lighting — Bosso Smart Lighting", to: "/outdoor-lighting" },
      { label: "Outdoor Heating & Cooling", to: "/outdoor-hvac" },
      { label: "Outdoor Misting Cooling — Aeromist", to: "/outdoor-cooling-misting" },
      { label: "Smart Automation & Controls", to: "/lone-star-elite" },
    ],
  },
  {
    slug: "bluebonnet-series",
    path: "/bluebonnet-series",
    name: "Bluebonnet",
    fullName: "Bluebonnet Series™",
    market: "Tents, Vendor Booths & Outdoor Markets",
    audience: "Vendors, boutiques, food trucks, farmers markets, festivals and event organizers.",
    group: "commercial",
    products: [
      { label: "Commercial Event Tents & Custom Canopies", to: "/commercial-event-tents" },
      { label: "Commercial Pop-Up Tents & Canopies", to: "/commercial-event-tents" },
      { label: "Custom Printed Tent Tops & Sidewalls", to: "/commercial-event-tents" },
      { label: "Vendor Booth & Boutique Setups", to: "/commercial-event-tents" },
      { label: "Farmers Market & Festival Tents", to: "/commercial-event-tents" },
      { label: "Table Covers, Banners & Flags", to: "/bluebonnet-series" },
      { label: "Tent Sidewalls & Enclosure Panels", to: "/commercial-event-tents" },
      { label: "Tent Repair, Recover & Parts", to: "/recover-awnings" },
    ],
  },
  {
    slug: "longhorn-series",
    path: "/longhorn-series",
    name: "Longhorn",
    fullName: "Longhorn Series™",
    market: "Commercial Shade Systems",
    audience: "Restaurants, retail centers, offices, developers, GCs and property managers.",
    group: "commercial",
    products: [
      { label: "Commercial Awnings", to: "/commercial-awnings" },
      { label: "Restaurant & Patio Awnings", to: "/restaurant-awnings" },
      { label: "Backlit / Illuminated Awnings", to: "/backlit-awnings" },
      { label: "Metal & Standing-Seam Awnings", to: "/metal-awnings" },
      { label: "Storefront & Walkway Canopies", to: "/commercial-awnings" },
      { label: "Commercial Drop Curtains", to: "/curtains" },
      { label: "Motorized Screens for Patios", to: "/exterior-vertical-shades" },
      { label: "Covered Parking", to: "/covered-parking" },
      { label: "Car Wash Shade Sails", to: "/car-wash-shade-sails" },
      { label: "Outdoor Heating & Cooling for Patios", to: "/outdoor-hvac" },
      { label: "Restaurant Patio Misting — Aeromist", to: "/outdoor-cooling-misting" },
    ],
  },
  {
    slug: "rio-grande-series",
    path: "/rio-grande-series",
    name: "Rio Grande",
    fullName: "Rio Grande Series™",
    market: "Hospitality & Resorts",
    audience: "Hotels, resorts, glamping operators, spas, retreats and event venues.",
    group: "commercial",
    products: [
      { label: "Resort & Hotel Cabanas", to: "/rio-grande-series" },
      { label: "Glamping & Safari Tents", to: "/safari-glamping-tents" },
      { label: "Spa & Wellness Shade", to: "/rio-grande-series" },
      { label: "Hotel & Restaurant Patio Awnings", to: "/restaurant-awnings" },
      { label: "Patio Enclosures & Poolside Drop Curtains", to: "/curtains" },
      { label: "Pool & Spa Shade Structures", to: "/pool-spa" },
      { label: "Glamping Tent Repairs & Recover", to: "/safari-glamping-tents" },
      { label: "Resort Misting & Outdoor Cooling", to: "/outdoor-cooling-misting" },
      { label: "Outdoor Heating & Cooling", to: "/outdoor-hvac" },
      { label: "Event & Venue Shade", to: "/sail-shades" },
    ],
  },
  {
    slug: "republic-series",
    path: "/republic-series",
    name: "Republic",
    fullName: "Republic Series™",
    market: "Government, Schools & Public Works",
    audience: "School districts, churches, cities, counties and government buyers.",
    group: "commercial",
    products: [
      { label: "Playground Shade", to: "/playground-shade" },
      { label: "School Shade Structures", to: "/school-shade-structures" },
      { label: "Church Shade Structures", to: "/church-shade-structures" },
      { label: "Athletic, Dugout & Bleacher Shade", to: "/school-shade-structures" },
      { label: "Covered Walkways & Bus Canopies", to: "/covered-parking" },
      { label: "Windscreens & Pit Covers", to: "/covers-tops" },
      { label: "Municipal Awnings & Signage Canopies", to: "/commercial-awnings" },
    ],
  },
  {
    slug: "farming-ranching-series",
    path: "/farming-ranching-series",
    name: "Farming & Ranching",
    fullName: "Farming & Ranching Series™",
    market: "Agricultural Covers & Ranch Fabrication",
    audience: "Ranchers, farmers, stock show families and ag operations across Texas.",
    group: "ag-rec",
    products: [
      { label: "Stock Trailer Covers", to: "/stock-trailer-covers" },
      { label: "Trailer Covers & Tarps", to: "/trailer-covers" },
      { label: "Wagon Covers", to: "/wagon-covers" },
      { label: "Hog Traps & Cages", to: "/hog-traps-and-cages" },
      { label: "Livestock & Barn Shade", to: "/farming-ranching-series" },
      { label: "Equipment & Implement Covers", to: "/covers-tops" },
      { label: "Welding & Custom Fabrication", to: "/custom-fabrication" },
      { label: "Trailer Wiring", to: "/trailer-wiring" },
    ],
  },
  {
    slug: "outdoor-recreation-series",
    path: "/outdoor-recreation-series",
    name: "Outdoor Recreation",
    fullName: "Outdoor Recreation Series™",
    market: "Hunting, Fishing & Boating",
    audience: "Hunters, anglers, boaters, lease owners and RV travelers.",
    group: "ag-rec",
    products: [
      { label: "Boat Covers & Bimini Tops", to: "/boat-covers" },
      { label: "RV Covers", to: "/rv-covers" },
      { label: "Hunting & Fishing Covers", to: "/hunting-fishing-covers" },
      { label: "Deer Blind Covers", to: "/deer-blind-covers" },
      { label: "Cooler, Gun & Gear Covers", to: "/cooler-gun-gear-covers" },
      { label: "Dog Boxes", to: "/dog-boxes" },
      { label: "Marine Upholstery & Cushions", to: "/covers-tops" },
    ],
  },
  {
    slug: "revival-series",
    path: "/revival-series",
    name: "Revival",
    fullName: "Revival Series™",
    market: "Service, Recover & Repair",
    audience: "Anyone with a tired awning, torn cover or aging retractable system.",
    group: "specialty",
    products: [
      { label: "Awning & Canopy Recover", to: "/recover-awnings" },
      { label: "Retractable Awning Service & Motor Repair", to: "/retractable-awnings" },
      { label: "Boat Cover & Bimini Repair", to: "/boat-covers" },
      { label: "Zipper, Thread & Fabric Repair", to: "/revival-series" },
      { label: "Frame Repair & Welding", to: "/custom-fabrication" },
      { label: "Fabric Replacement & Upgrades", to: "/fabrics" },
      { label: "Tent & Glamping Structure Repair", to: "/safari-glamping-tents" },
      { label: "Event Tent Top Recover", to: "/commercial-event-tents" },
    ],
  },
  {
    slug: "lone-star-elite",
    path: "/lone-star-elite",
    name: "Lone Star Series",
    fullName: "Lone Star Series™",
    market: "Smart Lighting & Automation",
    audience: "Homes and businesses that want app-controlled shade and lighting.",
    group: "specialty",
    products: [
      { label: "Bosso Smart Lighting", to: "/outdoor-lighting" },
      { label: "Automated Retractable Awnings", to: "/retractable-awnings" },
      { label: "Motorized Screens & Shades", to: "/exterior-vertical-shades" },
      { label: "Motorized Louvered Pergola Controls", to: "/louvered-pergolas" },
      { label: "Lighting Controls & Scenes", to: "/outdoor-lighting" },
      { label: "Remote & App Control", to: "/lone-star-elite" },
      { label: "Wind & Sun Sensors", to: "/lone-star-elite" },
    ],
  },
  {
    slug: "trail-boss",
    path: "/custom-fabrication",
    name: "Trail Boss",
    fullName: "Trail Boss™",
    market: "Welding & Custom Fabrication",
    audience: "Ranchers, trailer owners, contractors and anyone needing custom metal work.",
    group: "specialty",
    products: [
      { label: "Welding & Custom Fabrication", to: "/custom-fabrication" },
      { label: "Awning & Canopy Frames", to: "/metal-awnings" },
      { label: "Custom Bending & In-House Steel Work", to: "/custom-fabrication" },
      { label: "Trailer & Frame Repair", to: "/trailer-covers" },
      { label: "Trailer Wiring", to: "/trailer-wiring" },
      { label: "Hog Traps, Cages & Racks", to: "/hog-traps-and-cages" },
    ],
  },
  {
    slug: "ranger-care",
    path: "/ranger-care",
    name: "Ranger Care",
    fullName: "Ranger Care™",
    market: "Maintenance Memberships",
    audience: "Owners who want their shade cleaned, inspected and storm-ready year round.",
    group: "specialty",
    products: [
      { label: "Annual Maintenance Memberships", to: "/ranger-care" },
      { label: "Fabric Cleaning & Protectant", to: "/ranger-care" },
      { label: "Hardware, Motor & Frame Inspection", to: "/ranger-care" },
      { label: "Storm Prep & Seasonal Retraction", to: "/ranger-care" },
      { label: "Priority Service Scheduling", to: "/ranger-care" },
      { label: "Recover & Repair Discounts", to: "/recover-awnings" },
    ],
  },
];

export function getSeriesProducts(slug: string): SeriesProductEntry | undefined {
  return SERIES_PRODUCTS.find((s) => s.slug === slug);
}

/** Compact "what's under this badge" summary line for badge tiles / menus. */
export function seriesProductSummary(slug: string, max = 4): string {
  const entry = getSeriesProducts(slug);
  if (!entry) return "";
  return entry.products
    .slice(0, max)
    .map((p) => p.label.replace(/\s*—.*$/, "").replace(/\s*\/.*$/, ""))
    .join(" • ");
}

/** How many products / services live under a badge. */
export function seriesProductCount(slug: string): number {
  return getSeriesProducts(slug)?.products.length ?? 0;
}
